// src/pages/event/EventPage.jsx
import React, { useEffect, useRef, useState } from 'react'
import { START_DATE, TRACKS } from '../../data/eventTracks'
import { PHOTOS, BASE_PATH } from '../../data/eventPhotos'
import SmallPhotoArc from '../../components/event/SmallPhotoArc'
import FloatingHearts from '../../components/event/FloatingHearts'
import EventHeader from '../../components/event/EventHeader'
import NowPlayingPanel from '../../components/event/NowPlayingPanel'
import axiosInstance from '@/services/axiosInstance'

// 한국(KST) 기준 오늘 날짜 기준으로 몇 주년인지 계산
const getAnnivNumber = () => {
    const baseKst = new Date(`${START_DATE}T00:00:00+09:00`)
    const now = new Date()
    const kstNow = new Date(
        now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
    )

    let diff = kstNow.getFullYear() - baseKst.getFullYear()

    const annivThisYear = new Date(
        kstNow.getFullYear(),
        baseKst.getMonth(),
        baseKst.getDate(),
    )

    if (kstNow < annivThisYear) diff -= 1
    if (diff < 1) return 1
    return diff
}

const EventPage = () => {
    const annivNumber = getAnnivNumber()

    const [index, setIndex] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [fadeKey, setFadeKey] = useState(0)
    const [trackIndex, setTrackIndex] = useState(0)
    const [checking, setChecking] = useState(true) // ✅ 이벤트 권한 검증 중
    const audioRef = useRef(null)

    const currentPhoto = PHOTOS[index]

    const toggleMusic = () => {
        setIsPlaying((prev) => !prev)
    }

    const goPrev = () => {
        setIndex((prev) => {
            const next = (prev - 1 + PHOTOS.length) % PHOTOS.length
            setFadeKey((k) => k + 1)
            return next
        })
    }

    const goNext = () => {
        setIndex((prev) => {
            const next = (prev + 1) % PHOTOS.length
            setFadeKey((k) => k + 1)
            return next
        })
    }

    // ✅ 이벤트 접근 권한 검증 (백엔드 /api/event/access)
    useEffect(() => {
        const checkAccess = async () => {
            try {
                await axiosInstance.get('/api/event/access')
                // 200이면 우리 둘 → 자동 재생
                setIsPlaying(true)
            } catch (e) {
                // 401 / 403 은 axiosInstance 인터셉터에서
                // /logout?type=token / noAuth 로 리다이렉트
            } finally {
                setChecking(false)
            }
        }

        checkAccess()
    }, [])

    // 타이틀 변경
    useEffect(() => {
        const prevTitle = document.title
        document.title = `${annivNumber}년의 기록`
        return () => {
            document.title = prevTitle
        }
    }, [annivNumber])

    // 오디오 컨트롤
    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        audio.src = TRACKS[trackIndex].src

        if (isPlaying) {
            audio
                .play()
                .catch(() => {
                    setIsPlaying(false)
                })
        } else {
            audio.pause()
        }
    }, [trackIndex, isPlaying])

    // 곡 끝나면 다음 트랙
    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        const handleEnded = () => {
            setTrackIndex((prev) => (prev + 1) % TRACKS.length)
        }

        audio.addEventListener('ended', handleEnded)
        return () => {
            audio.removeEventListener('ended', handleEnded)
        }
    }, [])

    // 키보드 좌우 화살표 네비게이션
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault()
                setIndex((prev) => {
                    const next = (prev - 1 + PHOTOS.length) % PHOTOS.length
                    setFadeKey((k) => k + 1)
                    return next
                })
            } else if (e.key === 'ArrowRight') {
                e.preventDefault()
                setIndex((prev) => {
                    const next = (prev + 1) % PHOTOS.length
                    setFadeKey((k) => k + 1)
                    return next
                })
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [])

    // ✅ 훅들 다 호출된 다음에 조건부 렌더
    if (checking) {
        return (
            <div className="flex h-screen items-center justify-center bg-black text-sm text-slate-200">
                잠깐만 기다려줘요...
            </div>
        )
    }

    return (
        <div>
            {/* BGM 오디오 */}
            <audio ref={audioRef} loop={false} />

            {/* 배경 */}
            <div className="pointer-events-none fixed inset-0 -z-30 bg-gradient-to-b from-black via-slate-950 to-black" />
            <div className="pointer-events-none fixed inset-0 -z-20 bg-[radial-gradient(circle_at_10%_20%,#6366f1_0,#000_55%),radial-gradient(circle_at_90%_80%,#fb7185_0,#000_55%)] opacity-[0.2]" />

            <FloatingHearts />

            <EventHeader
                annivNumber={annivNumber}
                isPlaying={isPlaying}
                onToggleMusic={toggleMusic}
            />

            <main className="relative flex justify-center pt-16 pb-10 sm:pt-20">
                {/* 현재 사진 기반 블러 배경 */}
                <div className="absolute inset-0 -z-10">
                    <div
                        className="h-full w-full scale-110 bg-cover bg-center blur-[18px] opacity-70"
                        style={{
                            backgroundImage: `url(${BASE_PATH}/${currentPhoto.file})`,
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/90" />
                </div>

                {/* 실제 컨테이너: 가운데 정렬 + 폭 고정 */}
                <div className="relative w-full max-w-6xl px-4 sm:px-6 lg:px-10 box-border">
                    <div className="grid w-full max-w-full grid-cols-1 items-start gap-4 sm:gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]">
                        {/* 왼쪽: 사진 카드 */}
                        <div>
                            <div className="mx-auto max-w-4xl rounded-[2.1rem] bg-gradient-to-br from-rose-500/80 via-fuchsia-500/50 to-sky-500/60 p-[1.5px] shadow-[0_30px_120px_rgba(0,0,0,0.9)]">
                                <SmallPhotoArc
                                    currentIndex={index}
                                    onSelect={(i) => {
                                        setIndex(i)
                                        setFadeKey((k) => k + 1)
                                    }}
                                />
                                <div
                                    key={fadeKey}
                                    className="animate-fadeMemory flex flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-black/70 backdrop-blur-2xl md:max-h-[72vh]"
                                >
                                    <div className="relative flex-1 bg-black">
                                        <img
                                            src={`${BASE_PATH}/${currentPhoto.file}`}
                                            alt={currentPhoto.caption}
                                            className="mx-auto w-full max-h-[60vh] object-contain sm:max-h-[62vh] md:max-h-[60vh]"
                                        />

                                        <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2 sm:left-4 sm:top-4">
                                            <span className="rounded-full bg-black/60 px-2.5 py-1 text-[0.6rem] text-slate-200 backdrop-blur sm:px-3 sm:text-[0.65rem]">
                                                scene {index + 1}
                                            </span>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={goPrev}
                                            className="group absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-slate-100 backdrop-blur sm:left-3"
                                        >
                                            <span className="translate-x-[1px] text-xl group-active:-translate-x-[2px]">
                                                ‹
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={goNext}
                                            className="group absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-slate-100 backdrop-blur sm:right-3"
                                        >
                                            <span className="-translate-x-[1px] text-xl group-active:translate-x-[2px]">
                                                ›
                                            </span>
                                        </button>
                                    </div>

                                    <div className="border-t border-white/10 bg-black/80 px-4 py-4 text-xs text-slate-100 sm:px-6 sm:py-5 sm:text-sm">
                                        <p className="text-[0.68rem] text-slate-400 sm:text-[0.7rem]">
                                            {annivNumber}년째 진행중 ( 방향키로도 사진을 넘길 수
                                            있어요.)
                                        </p>
                                        <p className="mt-1 text-[0.9rem] leading-relaxed sm:text-[0.95rem]">
                                            {currentPhoto.caption}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 오른쪽: Now Playing */}
                        <NowPlayingPanel
                            audioRef={audioRef}
                            tracks={TRACKS}
                            trackIndex={trackIndex}
                            isPlaying={isPlaying}
                            onChangeTrack={setTrackIndex}
                        />
                    </div>

                    {/* 아래 설명 + 채팅 이동 */}
                    <div className="mt-6 flex flex-col items-center justify-between gap-4 rounded-2xl border border-white/5 bg-gradient-to-br from-slate-900/50 to-black/50 p-4 backdrop-blur-xl sm:mt-8 sm:flex-row sm:p-6">
                        <p className="text-center text-xs text-slate-300 sm:text-left sm:text-sm">
                            {annivNumber}년 동안 쌓아 온 흔적들, 한 장씩 넘기는 중…
                        </p>
                        <a
                            href="https://salcho.n-e.kr/chatList"
                            className="group relative inline-flex w-full items-center justify-center gap-3 overflow-hidden rounded-full bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 px-6 py-3 text-sm font-bold text-white shadow-[0_0_30px_rgba(244,114,182,0.6)] transition-all hover:scale-105 hover:shadow-[0_0_60px_rgba(244,114,182,0.8)] sm:w-auto"
                        >
                            <span className="relative z-10">우리 채팅 하러 가기</span>
                            <span className="relative z-10 text-lg transition-transform group-hover:scale-125">
                                💌
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 opacity-0 transition-opacity group-hover:opacity-100" />
                        </a>
                    </div>
                </div>
            </main>
        </div>
    )
}

export default EventPage
